/**
 * TanStack-Query-Client Singleton (W0.4)
 *
 * Wird in main.tsx als Provider-Instanz genutzt UND direkt im Auth-Store/Interceptor
 * für queryClient.clear() beim Logout — ohne React-Kontext.
 *
 * N1(a): Retry-Policy. Bei 429 (Rate-Limit) darf NICHT retryt werden — jeder
 * weitere Request belastet den Stundenzaehler des Backends (api_security.rb)
 * und verlaengert die Sperre. Auch andere 4xx (ausser 408/425) sind nicht
 * durch Wiederholen heilbar. 5xx/Netzwerkfehler werden weiterhin (begrenzt,
 * mit exponentiellem Backoff) wiederholt.
 */
import { QueryClient } from '@tanstack/react-query'
import { ApiError } from './errors/ApiError'

export const MAX_QUERY_RETRIES = 2

/** Statuscodes, bei denen ein Retry sinnvoll bleibt, obwohl sie 4xx sind. */
const RETRYABLE_CLIENT_STATUS = new Set([408, 425])

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    // 429 explizit: niemals automatisch wiederholen.
    if (error.isRateLimit) return false
    if (error.status >= 400 && error.status < 500 && !RETRYABLE_CLIENT_STATUS.has(error.status)) {
      return false
    }
  }
  return failureCount < MAX_QUERY_RETRIES
}

export function queryRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 30_000)
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 60s default
      retry: shouldRetryQuery,
      retryDelay: queryRetryDelay,
    },
    // Mutations bleiben bei React-Query-Default (retry: false) — insbesondere
    // wird ein 429 auf einer Mutation nie automatisch wiederholt.
    mutations: {
      retry: false,
    },
  },
})
